/**
 * 周易占卜引擎 - 公共类型定义
 */

// ── 天干 ──────────────────────────────────────────────────────
export const TIAN_GAN = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'] as const;
export type TianGan = typeof TIAN_GAN[number];

// ── 地支 ──────────────────────────────────────────────────────
export const DI_ZHI = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'] as const;
export type DiZhi = typeof DI_ZHI[number];

// ── 五行 ──────────────────────────────────────────────────────
export const WU_XING = ['木', '火', '土', '金', '水'] as const;
export type WuXing = typeof WU_XING[number];

/** 天干对应五行 */
export const GAN_WX: Record<TianGan, WuXing> = {
  '甲': '木', '乙': '木', '丙': '火', '丁': '火',
  '戊': '土', '己': '土', '庚': '金', '辛': '金',
  '壬': '水', '癸': '水',
};

/** 地支对应五行 */
export const ZHI_WX: Record<DiZhi, WuXing> = {
  '子': '水', '丑': '土', '寅': '木', '卯': '木',
  '辰': '土', '巳': '火', '午': '火', '未': '土',
  '申': '金', '酉': '金', '戌': '土', '亥': '水',
};

// ── 五行生克规则已迁移至 unifiedRules.ts ─────────────────────────
// 请统一从 '@/utils/calcTools/unifiedRules' 导入：
//   ELEMENT_GENERATES    (原 WX_SHENG_ME 的反向——谁被我生)
//   ELEMENT_OVERCOMES    (原 WX_KE_WHOM——我克谁)
//   ELEMENT_GENERATED_BY (原 WX_SHENG_ME——谁生我)
//   ELEMENT_OVERCOME_BY  (原 WX_KE_ME——谁克我)

// ── 八卦 ──────────────────────────────────────────────────────
export interface Bagua {
  name: string;        // 卦名
  symbol: string;      // 符号 ☰ ☱ ☲ ☳ ☴ ☵ ☶ ☷
  binary: number;      // 二进制 0-7（初爻→上爻）
  element: WuXing;
  nature: string;      // 天 泽 火 雷 风 水 山 地
  family: string;      // 父 少女 中女 长男 长女 中男 少男 母
  direction: string;
  number: number;      // 先天数
}

export const BAGUA_LIST: Bagua[] = [
  { name: '乾', symbol: '☰', binary: 7, element: '金', nature: '天', family: '父', direction: '西北', number: 1 },
  { name: '兑', symbol: '☱', binary: 3, element: '金', nature: '泽', family: '少女', direction: '正西', number: 2 },
  { name: '离', symbol: '☲', binary: 5, element: '火', nature: '火', family: '中女', direction: '正南', number: 3 },
  { name: '震', symbol: '☳', binary: 1, element: '木', nature: '雷', family: '长男', direction: '正东', number: 4 },
  { name: '巽', symbol: '☴', binary: 6, element: '木', nature: '风', family: '长女', direction: '东南', number: 5 },
  { name: '坎', symbol: '☵', binary: 2, element: '水', nature: '水', family: '中男', direction: '正北', number: 6 },
  { name: '艮', symbol: '☶', binary: 4, element: '土', nature: '山', family: '少男', direction: '东北', number: 7 },
  { name: '坤', symbol: '☷', binary: 0, element: '土', nature: '地', family: '母', direction: '正南', number: 8 },
];

/** 按 binary 值查找八卦 */
export function getBaguaByBinary(b: number): Bagua {
  return BAGUA_LIST.find(bg => bg.binary === b) ?? BAGUA_LIST[7];
}
/** 按名称查找八卦 */
export function getBaguaByName(name: string): Bagua {
  return BAGUA_LIST.find(bg => bg.name === name) ?? BAGUA_LIST[7];
}

// ── 64卦名表（上卦idx*8 + 下卦idx，按 BAGUA_LIST 顺序） ──────
export const HEXAGRAM_64: string[] = [
  '乾为天','天泽履','天火同人','天雷无妄','天风姤','天水讼','天山遁','天地否',
  '泽天夬','兑为泽','泽火革','泽雷随','泽风大过','泽水困','泽山咸','泽地萃',
  '火天大有','火泽睽','离为火','火雷噬嗑','火风鼎','火水未济','火山旅','火地晋',
  '雷天大壮','雷泽归妹','雷火丰','震为雷','雷风恒','雷水解','雷山小过','雷地豫',
  '风天小畜','风泽中孚','风火家人','风雷益','巽为风','风水涣','风山渐','风地观',
  '水天需','水泽节','水火既济','水雷屯','水风井','坎为水','水山蹇','水地比',
  '山天大畜','山泽损','山火贲','山雷颐','山风蛊','山水蒙','艮为山','山地剥',
  '地天泰','地泽临','地火明夷','地雷复','地风升','地水师','地山谦','坤为地',
];

// ── 64卦卦辞原文（周易原典）────────────────────────────────────
export const HEXAGRAM_GUACI: string[] = [
  '乾：元亨，利贞。', '履：履虎尾，不咥人，亨。', '同人：同人于野，亨，利涉大川，利君子贞。', '无妄：元亨，利贞，其匪正有眚，不利有攸往。',
  '姤：女壮，勿用取女。', '讼：有孚，窒惕，中吉，终凶，利见大人，不利涉大川。', '遁：亨，小利贞。', '否：否之匪人，不利君子贞，大往小来。',
  '夬：扬于王庭，孚号，有厉，告自邑，不利即戎，利有攸往。', '兑：亨，利贞。', '革：己日乃孚，元亨，利贞，悔亡。', '随：元亨，利贞，无咎。',
  '大过：栋桡，利有攸往，亨。', '困：亨，贞，大人吉，无咎，有言不信。', '咸：亨，利贞，取女吉。', '萃：亨，王假有庙，利见大人，亨，利贞，用大牲吉，利有攸往。',
  '大有：元亨。', '睽：小事吉。', '离：利贞，畜牝牛吉。', '噬嗑：亨，利用狱。',
  '鼎：元吉，亨。', '未济：亨，小狐汔济，濡其尾，无攸利。', '旅：小亨，旅贞吉。', '晋：康侯用锡马蕃庶，昼日三接。',
  '大壮：利贞。', '归妹：征凶，无攸利。', '丰：亨，王假之，勿忧，宜日中。', '震：亨，震来虩虩，笑言哑哑，震惊百里，不丧匕鬯。',
  '恒：亨，无咎，利贞，利有攸往。', '解：利西南，无所往，其来复吉，有攸往，夙吉。', '小过：亨，利贞，可小事，不可大事，飞鸟遗之音，不宜上，宜下，大吉。', '豫：利建侯行师。',
  '小畜：亨，密云不雨，自我西郊。', '中孚：豚鱼吉，利涉大川，利贞。', '家人：利女贞。', '益：利有攸往，利涉大川。',
  '巽：小亨，利有攸往，利见大人。', '涣：亨，王假有庙，利涉大川，利贞。', '渐：女归吉，利贞。', '观：盥而不荐，有孚颙若。',
  '需：有孚，光亨，贞吉，利涉大川。', '节：亨，苦节不可贞。', '既济：亨，小利贞，初吉终乱。', '屯：元亨，利贞，勿用有攸往，利建侯。',
  '井：改邑不改井，无丧无得，往来井井，汔至亦未繘井，羸其瓶，凶。', '坎：有孚，维心亨，行有尚。', '蹇：利西南，不利东北，利见大人，贞吉。', '比：吉，原筮，元永贞，无咎，不宁方来，后夫凶。',
  '大畜：利贞，不家食吉，利涉大川。', '损：有孚，元吉，无咎，可贞，利有攸往，曷之用，二簋可用享。', '贲：亨，小利有攸往。', '颐：贞吉，观颐，自求口实。',
  '蛊：元亨，利涉大川，先甲三日，后甲三日。', '蒙：亨，匪我求童蒙，童蒙求我，初筮告，再三渎，渎则不告，利贞。', '艮：艮其背，不获其身，行其庭，不见其人，无咎。', '剥：不利有攸往。',
  '泰：小往大来，吉亨。', '临：元亨，利贞，至于八月有凶。', '明夷：利艰贞。', '复：亨，出入无疾，朋来无咎，反复其道，七日来复，利有攸往。',
  '升：元亨，用见大人，勿恤，南征吉。', '师：贞，大人吉，无咎。', '谦：亨，君子有终。', '坤：元亨，利牝马之贞，君子有攸往，先迷后得主，利西南得朋，东北丧朋，安贞吉。',
];

// ── 64卦象辞原文（周易原典）────────────────────────────────────
export const HEXAGRAM_XIANGCI: string[] = [
  '天行健，君子以自强不息。', '上天下泽，履；君子以辩上下，定民志。', '天与火，同人；君子以类族辨物。', '天下雷行，物与无妄；先王以茂对时育万物。',
  '天下有风，姤；后以施命诰四方。', '天与水违行，讼；君子以作事谋始。', '天下有山，遁；君子以远小人，不恶而严。', '天地不交，否；君子以俭德辟难，不可荣以禄。',
  '泽上于天，夬；君子以施禄及下，居德则忌。', '丽泽，兑；君子以朋友讲习。', '泽中有火，革；君子以治历明时。', '泽中有雷，随；君子以向晦入宴息。',
  '泽灭木，大过；君子以独立不惧，遁世无闷。', '泽无水，困；君子以致命遂志。', '山上有泽，咸；君子以虚受人。', '泽上于地，萃；君子以除戎器，戒不虞。',
  '火在天上，大有；君子以遏恶扬善，顺天休命。', '上火下泽，睽；君子以同而异。', '明两作，离；大人以继明照于四方。', '雷电，噬嗑；先王以明罚敕法。',
  '木上有火，鼎；君子以正位凝命。', '火在水上，未济；君子以慎辨物居方。', '山上有火，旅；君子以明慎用刑而不留狱。', '明出地上，晋；君子以自昭明德。',
  '雷在天上，大壮；君子以非礼弗履。', '泽上有雷，归妹；君子以永终知敝。', '雷电皆至，丰；君子以折狱致刑。', '洊雷，震；君子以恐惧修省。',
  '雷风，恒；君子以立不易方。', '雷水解；君子以赦过宥罪。', '山上有雷，小过；君子以行过乎恭，丧过乎哀，用过乎俭。', '雷出地奋，豫；先王以作乐崇德，殷荐之上帝，以配祖考。',
  '风行天上，小畜；君子以懿文德。', '泽上有风，中孚；君子以议狱缓死。', '风自火出，家人；君子以言有物而行有恒。', '风雷，益；君子以见善则迁，有过则改。',
  '随风，巽；君子以申命行事。', '风行水上，涣；先王以享于帝立庙。', '山上有木，渐；君子以居贤德善俗。', '风行地上，观；先王以省方观民设教。',
  '云上于天，需；君子以饮食宴乐。', '泽上有水，节；君子以制数度，议德行。', '水在火上，既济；君子以思患而豫防之。', '云雷，屯；君子以经纶。',
  '木上有水，井；君子以劳民劝相。', '水洊至，习坎；君子以常德行，习教事。', '山上有水，蹇；君子以反身修德。', '地上有水，比；先王以建万国，亲诸侯。',
  '天在山中，大畜；君子以多识前言往行，以畜其德。', '山下有泽，损；君子以惩忿窒欲。', '山下有火，贲；君子以明庶政，无敢折狱。', '山下有雷，颐；君子以慎言语，节饮食。',
  '山下有风，蛊；君子以振民育德。', '山下出泉，蒙；君子以果行育德。', '兼山，艮；君子以思不出其位。', '山附于地，剥；上以厚下安宅。',
  '天地交，泰；后以财成天地之道，辅相天地之宜，以左右民。', '地上有泽，临；君子以教思无穷，容保民无疆。', '明入地中，明夷；君子以莅众，用晦而明。', '雷在地中，复；先王以至日闭关，商旅不行，后不省方。',
  '地中生木，升；君子以顺德，积小以高大。', '地中有水，师；君子以容民畜众。', '地中有山，谦；君子以裒多益寡，称物平施。', '地势坤，君子以厚德载物。',
];

// ── 64卦白话解释（现代语言）────────────────────────────────────
export const HEXAGRAM_BAIHUA: string[] = [
  '象征刚健纯粹、自强不息。天道运行不止，君子应效法天道，不断进取，永不停歇。', '象征谨慎行事、以柔克刚。如同踩着老虎尾巴，只要行为端正，就不会被咬伤。',
  '象征志同道合、共同奋进。与志同道合的人在广阔天地间合作，可以成就大事。', '象征纯真无妄、顺天而行。保持内心纯真，不妄为，顺应自然规律，诸事可成。',
  '象征阴阳相遇、机缘难得。天下有风，阴阳相遇，这是难得的机缘，需要把握。', '象征争讼纠纷、宜和不宜争。有争议时，宜寻求调解，不宜强行对抗，以和为贵。',
  '象征见机而退、以待时机。刚健遇到险阻，应当适时退隐，等待时机再行动。', '象征天地不交、否塞不通。上下不通，事情受阻，应守正待时，静候转机。',
  '象征决断刚健、除去小人。在朝廷上公开宣示，以正义决断，排除阻碍，顺势而为。', '象征喜悦和谐、处事顺遂。两泽相连，喜悦满溢，以柔和之道处事，万事顺遂。',
  '象征革故鼎新、顺天应人。在适当时机进行变革，顺应天意民心，大变革可以成功。', '象征随时从势、虽随而固。顺应时势变化，但内心保持正直，随而不失其正。',
  '象征刚过于中、需知节度。事情超过了正常限度，需要调整，不可过于偏激。', '象征困而不馁、穷则变通。处于困境中，君子不失其志，穷则思变，终能通达。',
  '象征男女交感、情意相通。山泽通气，男女相感，以诚相待，感情自然融洽。', '象征人群相聚、和气致祥。众人聚集，以诚信为本，和气相处，共同繁荣。',
  '象征光明灿烂、积善成德。拥有大量财富，应当广施恩德，积善成德，万物归心。', '象征异中求同、殊途同归。虽然表面有差异，但通过沟通理解，终能找到共同点。',
  '象征光明正大、文明内外。如同太阳照耀，内外文明，以正直之道处世，吉祥如意。', '象征强力推进、断案宜严。遇到阻碍需要强力突破，处理纠纷时应当明察秋毫。',
  '象征变革成器、调和鼎新。如同鼎中烹饪，通过变革和调和，成就新的事业。', '象征事未完成、稳步推进。虽然还未完全成功，但只要稳步前进，终能达成目标。',
  '象征旅行在外、谦逊慎行。在外旅行或处于陌生环境，应当谦逊谨慎，方能平安。', '象征逐渐上升、发展推进。如同太阳升起，事业逐渐发展，贵人相助，前途光明。',
  '象征威势浩大、勿以强违正。力量强大，但不可以强凌弱，应当以正道行事。', '象征嫁娶之象、阴阳调和。柔顺乘刚，阴阳调和，感情婚姻之事宜顺势而为。',
  '象征丰盛富足、把握时机。如同正午太阳，光明丰盛，应当把握最佳时机行动。', '象征震动奋发、自强不息。雷声震动，令人警醒，应当以此为戒，奋发自强。',
  '象征持久有成、刚柔相济。风雷相益，刚柔相济，持之以恒，终能成就大事。', '象征缓解险难、顺应民心。雷水解难，困难得以化解，应当广施恩惠，顺应民心。',
  '象征小有过失、知节而止。有些小的过失，应当及时纠正，知道适可而止。', '象征顺动豫乐、建功立业。顺应时势，积极行动，可以建立功业，获得成就。',
  '象征蓄积待发、时机未到。如同密云不雨，力量在积蓄，等待最佳时机再行动。', '象征诚信为本、感格天地。以诚信为根本，真诚待人，可以感动天地，成就大事。',
  '象征家庭和睦、内外有序。家庭内部和谐有序，女性守正，男性尽责，家道兴旺。', '象征损上益下、广施恩惠。减少上层的消耗，增益下层的利益，顺应天道，广施恩惠。',
  '象征随风俱进、申命行事。如同风的流动，顺势而为，宣布命令，推行政事。', '象征离散解结、重建秩序。如同风行水上，化解积聚的矛盾，重新建立和谐秩序。',
  '象征循序渐进、稳步发展。如同山上的树木，一步一步地生长，稳步发展，终能成材。', '象征仰观天文、俯察地理。观察天地万物的规律，以此教化民众，治理国家。',
  '象征等待时机、诚信顺待。如同云在天上，等待时机降雨，以诚信之心等待，终能成功。', '象征适度节制、量力而行。如同水在泽上，适度节制，不可过于苛刻，量力而行。',
  '象征大功告成、防患未然。水火相济，大功告成，但要居安思危，防患于未然。', '象征艰难创业、奠定基础。如同云雷交加，创业艰难，但只要坚持，终能奠定基础。',
  '象征滋养众生、取之不尽。如同井水养人，取之不尽，用之不竭，造福众生。', '象征险而又险、维心亨通。重重险难，但只要内心诚信，保持正道，终能通达。',
  '象征险而止步、修德待时。遇到险阻，应当停下来反思，修养德行，等待时机。', '象征亲辅比附、和谐共处。如同水在地上，亲近辅助，和谐共处，共同发展。',
  '象征积德蓄才、厚积薄发。如同天在山中，积累德行和才能，厚积薄发，终能成就大事。', '象征损己益人、惩忿窒欲。减少自己的欲望，增益他人，克制愤怒，修养品德。',
  '象征文采斑斓、内外兼修。如同山下有火，文采斑斓，内外兼修，以文明之道处世。', '象征颐养之道、慎言节食。如同山下有雷，颐养之道，谨慎言语，节制饮食。',
  '象征振救弊乱、革故鼎新。如同山下有风，振救弊乱，革除旧弊，建立新秩序。', '象征童蒙求学、果行育德。如同山下出泉，童蒙求学，以果断的行动培育德行。',
  '象征安止坚定、思不出位。如同两山相叠，安止坚定，思考不超出自己的职责范围。', '象征小人得势、守正待时。山附于地，小人得势，君子应当守正，等待时机。',
  '象征天地交泰、万物通顺。天地相交，万物通顺，上下沟通，政通人和，吉祥亨通。', '象征临近亲抚、教化无穷。如同地上有泽，临近亲抚，教化民众，恩泽无穷。',
  '象征光藏有害、艰贞守正。如同太阳入地，光明被遮蔽，应当艰难守正，以待时机。', '象征周而复始、阳气回升。如同雷在地中，阳气回升，万物复苏，新的开始。',
  '象征顺势而升、积小成大。如同地中生木，顺势而升，积累小的成就，终能成就大事。', '象征统帅三军、以正行兵。如同地中有水，统帅众人，以正义之道行事，终能成功。',
  '象征谦虚为德、功成名就。如同地中有山，谦虚低调，以德服人，终能功成名就。', '象征厚德载物、顺应自然。大地宽广，厚德载物，顺应自然规律，包容万物。',
];

// ── 64卦辞简解 ─────────────────────────────────────────────────
export const HEXAGRAM_DESCS: string[] = [
  '刚健纯粹，自强不息，万物资始，大通而利正。',
  '上天下泽，履险蹈危，以柔履刚，临事不惧。',
  '同人于野，亨通顺畅，利涉大川，贵人相助。',
  '无妄刚健，天命所宜，行正无妄，诸事自成。',
  '天下有风，相遇而来，阴阳相遇，机缘难得。',
  '刚健险难，据理力争，诉讼事宜，宜和不宜争。',
  '天山相遇，刚健中止，见机而退，以待时机。',
  '天地不交，否塞不通，守正待时，静候转机。',
  '决断刚健，夬除小人，决而不躁，顺势而为。',
  '两泽相丽，喜悦满溢，柔和说之，处事顺遂。',
  '革故鼎新，顺天应人，大变有时，把握时机。',
  '随时从势，虽随而固，顺应变化，刚中有正。',
  '泽风大过，刚过于中，矫枉过正，需知节度。',
  '泽水相遇，困而不馁，君子处困，穷则变通。',
  '山泽通气，男女交感，柔在上位，感而后应。',
  '泽地相聚，萃聚亨通，人群相聚，和气致祥。',
  '大有光明，内刚外顺，积善成德，万物归心。',
  '火泽睽异，异中求同，异途殊归，终见通达。',
  '光明灿烂，两耀相丽，文明内外，柔中顺正。',
  '口中有物，强咬成卦，刚柔交合，断案宜严。',
  '鼎中调和，变革成器，以木巽火，烹饪六爻。',
  '水火不济，事未完成，虽未全功，稳步而进。',
  '山上有火，旅行在外，羁旅孤寂，谦逊慎行。',
  '火在地上，晋升升华，受命光明，大展宏图。',
  '雷震天上，威势浩大，大壮刚强，勿以强违正。',
  '雷泽交合，嫁娶之象，柔乘刚行，阴阳调和。',
  '雷火相遇，丰盛富足，明动相资，丰亨有余。',
  '阳刚震动，威威不已，动而有恒，奋发自强。',
  '风雷相益，上下相应，刚柔相济，持久有成。',
  '雷水解难，缓解险难，解则布施，顺应民心。',
  '雷山相遇，小有过失，柔以制刚，知节而止。',
  '雷地豫悦，顺动豫乐，中正而应，刚应而志行。',
  '风天小畜，蓄而未发，柔得位中，可以推行。',
  '风泽中孚，诚信为本，以诚感人，感格天地。',
  '风火相协，家内和顺，女正位乎内，男正位乎外。',
  '风雷增益，损上益下，上施民从，天施地行。',
  '两风相重，随风俱进，重巽以申，主令之行。',
  '风水相涣，离散解结，涣奔其机，王假有庙。',
  '山风渐进，循序渐进，女归吉，利贞守正。',
  '风地相观，观天文化，仰观于天，俯察于地。',
  '水天相需，待机而动，诚信顺待，险中有亨。',
  '水泽相节，适度节制，苦节不可，自苦为节。',
  '水火相济，大功告成，小事亨通，终则有乱。',
  '水雷相屯，初生险难，君子以经纶，奠定基础。',
  '水风相济，井水养人，往来井井，无丧无得。',
  '两坎相重，险而又险，习坎有孚，维心亨通。',
  '水山蹇难，险而止步，蹇利西南，见大人吉。',
  '水地相比，亲辅比附，辅弼聚合，元永贞吉。',
  '山天大畜，蓄积大德，利涉大川，功成名就。',
  '山泽损益，损下益上，有孚元吉，可贞无咎。',
  '山火相饰，文采斑斓，白贲无咎，亨文明正。',
  '山雷颐养，口腹之道，观其所养，自求口实。',
  '山风相蛊，振救弊乱，刚上柔下，先甲后甲。',
  '山水相蒙，童蒙求学，匪我求童蒙，童蒙求我。',
  '两艮相重，安止坚定，艮其背，不获其身。',
  '山地相剥，小人得势，剥床以足，蔑贞凶也。',
  '地天相泰，通泰亨顺，小往大来，天地交泰。',
  '地泽临观，临近亲抚，有孚顺若，大亨以正。',
  '地火明夷，光藏有害，利艰贞，内难而正志。',
  '地雷相复，周而复始，七日来复，天行也正。',
  '地风上升，顺势而升，南征吉，志行也正。',
  '地水师众，统帅三军，以正行兵，大君有命。',
  '地山相谦，谦虚为德，有终也正，天道下济。',
  '坤顺厚载，万物归顺，元亨利牝马之贞，大地宽广。',
];

// ── 通用哈希工具 ─────────────────────────────────────────────
export function h32(s: string): number {
  let v = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    v ^= s.charCodeAt(i);
    v = (Math.imul(v, 0x01000193)) >>> 0;
  }
  return v;
}

export function dateSeed(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}-${d.getSeconds()}`;
}

export function seededInt(seed: number, lo: number, hi: number): number {
  return lo + (seed % (hi - lo + 1));
}

// ── 日期 → 天干地支 ──────────────────────────────────────────
/** 用于获取当日日干，用于六亲六神计算。1900-01-01是甲戌日，干支循环60 */
export function getDayGanZhi(date: Date): { gan: TianGan; zhi: DiZhi; ganIdx: number; zhiIdx: number } {
  const base = new Date(1900, 0, 1); // 1900-01-01 甲戌 日干=甲(0) 日支=戌(10)
  const diff = Math.floor((date.getTime() - base.getTime()) / 86400000);
  const ganIdx = (0 + diff) % 10;
  const zhiIdx = (10 + diff) % 12;
  return { gan: TIAN_GAN[ganIdx], zhi: DI_ZHI[zhiIdx], ganIdx, zhiIdx };
}

/** 获取当月月干支 */
export function getMonthGanZhi(date: Date): { gan: TianGan; zhi: DiZhi } {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed
  // 寅月开始(农历正月=寅月)，这里用简化方法
  const monthOffset = (year - 1900) * 12 + month;
  const ganIdx = (2 + monthOffset) % 10;   // 1900年寅月=丙寅，丙=2
  const zhiIdx = (2 + monthOffset) % 12;   // 寅=2
  return { gan: TIAN_GAN[ganIdx], zhi: DI_ZHI[zhiIdx] };
}

/** 获取年干支 */
export function getYearGanZhi(year: number): { gan: TianGan; zhi: DiZhi } {
  const ganIdx = (year - 4) % 10;
  const zhiIdx = (year - 4) % 12;
  return { gan: TIAN_GAN[(ganIdx + 10) % 10], zhi: DI_ZHI[(zhiIdx + 12) % 12] };
}

// ── 时辰地支 ─────────────────────────────────────────────────
export const HOUR_ZHI: DiZhi[] = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
export function getHourZhi(hour: number): DiZhi {
  // 子时 23-01, 丑时 01-03...
  const idx = Math.floor(((hour + 1) % 24) / 2);
  return HOUR_ZHI[idx];
}
export function getHourZhiIdx(hour: number): number {
  return Math.floor(((hour + 1) % 24) / 2);
}
